/** Safe JSON parse core with large-integer preservation, hosted in `const/` so pure `utils/` leaves can value-import it. This leaf is the ONLY home of the large-integer quoting technique -- do not re-inline a copy at a consumer. */

/** Quote large integers in a JSON string to preserve precision: numbers with 16+ digits beyond the safe-integer range become quoted strings so `JSON.parse` does not round large record IDs. Only matches JSON value positions. */
function quoteLargeIntegers(str: string): string {
	return str.replace(/([:,[]\s*)(-?\d{16,})(?=\s*[,}\]])/g, (match, prefix: string, digits: string) => {
		const num = Number(digits);
		if (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER) {
			return `${prefix}"${digits}"`;
		}
		return match;
	});
}

/** Parse a JSON string, returning `fallback` on any parse failure. With `preserveLargeIntegers` (the default) the
 *  input is pre-processed by `quoteLargeIntegers`. @runtime-derived-ssot-helper: pure-leaf parse core pinned below the utils layer floor. */
export function parseJsonSafe(str: string, fallback: unknown = null, preserveLargeIntegers = true): unknown {
	try {
		return JSON.parse(preserveLargeIntegers ? quoteLargeIntegers(str) : str);
		// Intentional: return fallback for invalid JSON
	} catch {
		return fallback;
	}
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the already-parsed-OR-JSON-string dispatch over
 * {@link parseJsonSafe}, homed beside its parse core so pure utils/ leaves can value-import it without a forbidden
 * cross-utils sibling dependency. Canonical unknown-to-Record payload coercion: an already-parsed object passes
 * through, a JSON string is parsed, and everything else (non-string, `''`, parse failure, non-object JSON) becomes
 * an empty record -- it NEVER throws, because a malformed payload column is a legitimate absence for that row.
 *
 * It lives HERE rather than in a caller because the `preserveLargeIntegers` argument is the load-bearing part: D1
 * TEXT payload columns hold 16+-digit record IDs that a plain `JSON.parse` silently rounds. A re-inlined dispatch
 * that forgets it corrupts IDs with no error -- the exact failure this leaf's "ONLY home" note exists to prevent.
 */
export function readPayload(payload: unknown): Record<string, unknown> {
	if (payload && typeof payload === 'object') return payload as Record<string, unknown>;
	if (typeof payload !== 'string' || payload === '') return {};
	return parseJsonSafe(payload, {}, true) as Record<string, unknown>;
}
